/**
 * Kuvr Expandable Sections
 * Accordion-style sections with AJAX-loaded content
 * Used for Persuasion & Validation content (Cost Over Time, 10 Reasons, Why Standard Fails, FAQ)
 */

class KuvrExpandable {
  constructor() {
    this.sections = [];
    this.loadedUrls = new Set();
    this.init();
  }

  init() {
    // Find all expandable sections
    this.sections = document.querySelectorAll('.kuvr-expandable');

    if (this.sections.length === 0) return;

    // Bind click events
    this.sections.forEach(section => {
      const header = section.querySelector('.kuvr-expandable__header');
      if (header) {
        header.addEventListener('click', (e) => this.toggle(section));
      }
    });
  }

  async toggle(section) {
    const isOpen = section.classList.contains('is-open');
    const content = section.querySelector('.kuvr-expandable__content');
    const url = section.getAttribute('data-content-url');

    if (isOpen) {
      // Close section
      this.close(section);
    } else {
      // Open section
      await this.open(section, content, url);
    }
  }

  async open(section, content, url) {
    // If content hasn't been loaded yet, fetch it
    if (url && !this.loadedUrls.has(url)) {
      await this.loadContent(section, content, url);
    }

    // Expand the section
    section.classList.add('is-open');

    // Set height for animation
    const inner = content.querySelector('.kuvr-expandable__inner');
    if (inner) {
      content.style.maxHeight = (inner.scrollHeight + 50) + 'px';
      // Once animation completes, remove the constraint so content is never clipped
      content.addEventListener('transitionend', function releaseHeight() {
        content.removeEventListener('transitionend', releaseHeight);
        if (section.classList.contains('is-open')) {
          content.style.maxHeight = 'none';
        }
      });
    }

    // Update aria
    const header = section.querySelector('.kuvr-expandable__header');
    if (header) {
      header.setAttribute('aria-expanded', 'true');
    }
  }

  close(section) {
    const content = section.querySelector('.kuvr-expandable__content');
    const inner = content.querySelector('.kuvr-expandable__inner');

    // If maxHeight was released to 'none', pin it explicitly before animating to 0
    if (content.style.maxHeight === 'none' || content.style.maxHeight === '') {
      content.style.maxHeight = (inner ? inner.scrollHeight + 50 : 1000) + 'px';
      content.getBoundingClientRect(); // force reflow
    }

    section.classList.remove('is-open');
    content.style.maxHeight = '0';

    // Update aria
    const header = section.querySelector('.kuvr-expandable__header');
    if (header) {
      header.setAttribute('aria-expanded', 'false');
    }
  }

  async loadContent(section, content, url) {
    const inner = content.querySelector('.kuvr-expandable__inner');

    // Show loading state
    inner.innerHTML = `
      <div class="kuvr-expandable__loading">
        <div class="kuvr-expandable__spinner"></div>
        <p>Loading...</p>
      </div>
    `;

    try {
      const response = await fetch(url);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const html = await response.text();

      // Parse HTML and extract content
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');

      // Look for .modal-article-content or .expandable-content wrapper
      const articleContent = doc.querySelector('.modal-article-content') ||
                            doc.querySelector('.expandable-content') ||
                            doc.querySelector('.page-content');

      if (articleContent) {
        inner.innerHTML = articleContent.innerHTML;
        this.loadedUrls.add(url);

        // Re-calculate height after content loads
        setTimeout(() => {
          content.style.maxHeight = (inner.scrollHeight + 50) + 'px';
        }, 10);
      } else {
        throw new Error('Content wrapper not found');
      }

    } catch (error) {
      console.error('Error loading expandable content:', error);
      inner.innerHTML = `
        <div class="kuvr-expandable__error">
          <p>Unable to load content. <a href="${url}">View page directly</a></p>
        </div>
      `;
    }
  }
}

// Initialize on DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new KuvrExpandable();
  });
} else {
  new KuvrExpandable();
}
